import { ColumnDef } from '@tanstack/react-table'
import { compact } from 'lodash'
import moment from 'moment'
import { NavigateFunction } from 'react-router-dom'
import { DateComponent } from 'app/components/DateComponent'
import { LazyRoleCell } from 'app/components/LazyRoleCell'
import { ActionsCell } from 'app/components/table/ActionsCell'
import { AnchorCell } from 'app/components/table/AnchorCell'
import ListColumn from 'app/components/table/ListColumn'
import { StaffAssignCellForUser } from 'app/components/table/StaffAssignCellForUser'
import { FilterableColumn, FilterValueType } from 'app/filter.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { UserJson } from 'app/types/entities'

export type UserColumnsProps = {
  me: UserJson
  /** Whether user can manage other users */
  canManageUsers: boolean
  /** Whether user is an org user */
  isOrgUser: boolean
  navigate: NavigateFunction
}

export const defaultColumns = ({
  me,
  canManageUsers,
  isOrgUser,
  navigate,
}: UserColumnsProps): ColumnDef<UserJson, any>[] => {
  return compact([
    {
      id: `name`,
      accessorKey: `name`,
      header: `Name`,
      meta: {
        defaultVisible: true,
        pinned: true,
        sortKey: `name.keyword`,
        getCellStyle: () => `h-12`,
        ControlledComponent: (row?: UserJson) => {
          if (!canManageUsers) return <ActionsCell content={row?.name ?? ``} />
          const href = `/settings/users/${row?.id}`
          return (
            <AnchorCell href={href} tooltipMessage={row?.name} clickHandler={() => navigate(href)}>
              <span>{row?.name}</span>
            </AnchorCell>
          )
        },
      },
    },
    {
      id: `email`,
      accessorKey: `email`,
      header: `Email`,
      meta: {
        defaultVisible: true,
        sortKey: `email.keyword`,
      },
    },
    me.type === `super` && {
      id: `type`,
      accessorFn: (row: UserJson) => row.type,
      header: `Type`,
      cell: (props: { getValue: () => string }) => (
        <span className="capitalize">{props.getValue().replace(`_`, ` `)}</span>
      ),
      meta: {
        defaultVisible: true,
        sortKey: `type.keyword`,
      },
    },
    me.type === `super` && {
      id: `resetCode`,
      accessorKey: `resetCode`,
      header: `Password Reset Code`,
      meta: {
        defaultVisible: true,
        sortKey: `resetCode.keyword`,
      },
    },
    me.type === `super` && {
      id: `loginCode`,
      accessorKey: `authCode`,
      header: `Login Code`,
      meta: {
        defaultVisible: true,
        sortKey: `authCode.keyword`,
      },
    },
    me.workspaceId &&
      canManageUsers && {
        id: `aliases`,
        accessorKey: `aliases`,
        header: `Aliases`,
        meta: {
          defaultVisible: true,
          sortKey: `aliases`,
        },
      },
    {
      id: `phone`,
      accessorKey: `phone`,
      accessorFn: (row) => formatPhoneNumber(row.phone ?? ``),
      header: `Phone Number`,
      meta: {
        defaultVisible: true,
        sortKey: `phone.keyword`,
      },
    },
    {
      id: `User Role`,
      accessorKey: `roleId`,
      header: `User Role`,
      cell: (props: { getValue: () => string; row: { original: UserJson } }) => {
        const user = props.row.original
        // Top-level roleId is always current for the user's active workspace (synced by updateUser).
        // For multi-workspace users viewing from a different workspace, use the workspaces entry instead.
        const roleId =
          user.workspaceId === me.workspaceId
            ? props.getValue()
            : (user.workspaces?.find((ws) => ws.id === me.workspaceId)?.roleId ?? props.getValue())
        return <LazyRoleCell roleId={roleId} />
      },
      meta: {
        defaultVisible: true,
        sortKey: `roleId`,
      },
    },
    {
      id: `associatedStaff`,
      header: `Associated Staff`,
      meta: {
        defaultVisible: false,
        sortKey: `id`,
        ControlledComponent: (row?: UserJson) =>
          row ? <StaffAssignCellForUser user={row} workspaceId={me.workspaceId!} /> : <></>,
      },
    },
    {
      id: `position`,
      accessorKey: `position`,
      header: `Position`,
      meta: {
        defaultVisible: true,
        sortKey: `jobTitle.keyword`,
      },
    },
    {
      id: `lastLoginDate`,
      accessorKey: `lastLoginDate`,
      accessorFn: (row: UserJson) => row.lastLoginDate || `-`,
      header: `Last Login Date`,
      minSize: 120,
      cell: (props: { getValue: () => Date | string }) => <DateComponent value={props.getValue()} useLocalUtc />,
      meta: {
        defaultVisible: true,
        sortKey: `lastLoginDate`,
      },
    },
    ...(me.workspaceId
      ? [
          {
            id: `locationIds`,
            accessorFn: (row: UserJson) =>
              row.workspaces?.find((workspace) => workspace.id === me.workspaceId)?.locationIds ??
              row.allowedLocationIds,
            header: `Locations`,
            meta: {
              defaultVisible: true,
            },
            enableSorting: false,
            cell: (props: { getValue: () => string[] }) => (
              <ListColumn ids={props.getValue()} entityName="locations" allText />
            ),
          },
        ]
      : []),
    isOrgUser && {
      id: `assignedWorkspaceIds`,
      accessorKey: `assignedWorkspaceIds`,
      header: `Workspaces`,
      meta: {
        defaultVisible: true,
      },
      enableSorting: false,
      cell: (props: { row: { original: UserJson } }) => (
        <ListColumn ids={props.row.original.assignedWorkspaceIds} entityName="workspaces" allText />
      ),
    },
  ]) as ColumnDef<UserJson, any>[]
}

export type FilterableColumnsProps = {
  showLastLoginDate?: boolean
}

export const filterableColumns = ({ showLastLoginDate = true }: FilterableColumnsProps = {}): FilterableColumn[] =>
  compact([
    { attribute: `name`, displayName: `Name`, type: FilterValueType.string },
    { attribute: `email`, displayName: `Email`, type: FilterValueType.string },
    { attribute: `phone`, displayName: `Phone`, type: FilterValueType.string },
    {
      attribute: `position`,
      displayName: `Position`,
      type: FilterValueType.multiple,
      options: [
        { name: `Executive`, id: `Executive`, value: `Executive` },
        { name: `Consultant`, id: `Consultant`, value: `Consultant` },
        { name: `Dentist`, id: `Dentist`, value: `Dentist` },
        { name: `Hygienist`, id: `Hygienist`, value: `Hygienist` },
        { name: `Manager`, id: `Manager`, value: `Manager` },
        { name: `Treatment Coordinator`, id: `Treatment Coordinator`, value: `Treatment Coordinator` },
        { name: `Administrator`, id: `Administrator`, value: `Administrator` },
        { name: `Other`, id: `Other`, value: `Other` },
      ],
    },
    showLastLoginDate && {
      attribute: `lastLoginDate`,
      displayName: `Last Login Date`,
      type: FilterValueType.rangeDate,
      defaultValue: [moment().startOf(`week`).format(`YYYY-MM-DD`), moment().endOf(`week`).format(`YYYY-MM-DD`)],
    },
  ]) as FilterableColumn[]
